import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LumaService, LumaCampaign, CampaignRecipient } from '../luma.service';
import { Inject } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Observable, from } from 'rxjs';
import { PermissionService } from '../../permission.service';

@Component({
  selector: 'app-luma-campaign-detail',
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.scss']
})
export class CampaignDetailComponent implements OnInit {
  campaign: LumaCampaign | null = null;
  isLoading = false;
  rows: CampaignRecipient[] = [];
  columns = ['generated_subject', 'recipient', 'created_at', 'updated_at', 'updated_by', 'actions'];
  page = 0;
  perPage = 25;
  count = 0;
  sendgridEnabled = false;
  canSend$!: Observable<boolean>;

  constructor(private route: ActivatedRoute, @Inject(LumaService) private luma: LumaService, private dialog: MatDialog, private permission: PermissionService) {}

  ngOnInit(): void {
    this.canSend$ = from(this.permission.categories.luma.hasSend());
    const id = this.route.snapshot.paramMap.get('id')!;
    this.isLoading = true;
    this.luma.getCampaign(id).subscribe({
      next: (c) => { this.campaign = c; this.isLoading = false; this.loadPage(0); this.checkSendgrid(); },
      error: () => { this.isLoading = false; }
    });
  }

  downloadZip(): void {
    if (!this.campaign) return;
    this.luma.downloadZip(this.campaign.id);
  }

  download(format: 'csv' | 'xlsx' | 'eml'): void {
    if (!this.campaign) return;
    this.luma.downloadZip(this.campaign.id, format);
  }

  checkSendgrid(): void {
    this.sendgridEnabled = false;
    this.luma.isSendgridEnabled().subscribe((r) => this.sendgridEnabled = !!r?.enabled);
  }

  openSendDialog(): void {
    if (!this.campaign) return;
    this.dialog.open(SendCampaignDialog, { width: '420px', data: { campaignId: this.campaign.id } }).afterClosed().subscribe((res) => {
      if (!res) return;
      // After setting sender info, refresh
      this.luma.getCampaign(this.campaign!.id).subscribe(c => this.campaign = c);
    });
  }

  loadPage(p: number): void {
    if (!this.campaign) return;
    this.page = p;
    this.luma.listRecipients(this.campaign.id, this.page, this.perPage).subscribe((res) => {
      this.rows = res.records;
      this.page = res.page;
      this.perPage = res.per_page;
      this.count = res.count;
    });
  }

  pageChange(e: PageEvent) { this.loadPage(e.pageIndex); }

  openPreview(r: CampaignRecipient): void {
    if (!this.campaign) return;
    this.luma.getRecipient(this.campaign.id, String(r.id)).subscribe(full => {
      this.dialog.open(CampaignRecipientPreviewDialog, { width: '90vw', maxWidth: '90vw', height: '90vh', data: { recipient: full, campaign: this.campaign } }).afterClosed().subscribe((updated) => {
        if (updated) { this.loadPage(this.page); }
      });
    });
  }
}

import { Component as NgComponent, Inject as NgInject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@NgComponent({
  selector: 'campaign-recipient-preview-dialog',
  template: `
  <h2 mat-dialog-title>Edit Email</h2>
  <div mat-dialog-content class="content">
    <mat-form-field appearance="fill" class="full">
      <mat-label>Subject</mat-label>
      <input matInput [(ngModel)]="subject" [readonly]="isReadOnly" />
    </mat-form-field>
    <div class="editor-wrap">
      <editor
        [apiKey]="'ck8ze3gv5uipup94hehaevo1z6p1h9eh4wzi0h86qad6xam6'"
        [init]="{ menubar: false }"
        [(ngModel)]="body"
        [disabled]="isReadOnly"
      ></editor>
    </div>
    <div class="meta" *ngIf="data.recipient.updated_by">
      <small>Last updated {{ data.recipient.updated_at | date:'short' }} by {{ data.recipient.updated_by_first_name }} {{ data.recipient.updated_by_last_name }} ({{ data.recipient.updated_by_email }})</small>
    </div>
  </div>
  <div mat-dialog-actions align="end">
    <button mat-button type="button" (click)="close()">Close</button>
    <button mat-raised-button color="lightGreen" (click)="update()" [disabled]="isReadOnly || saving">Update</button>
  </div>
  `,
  styles: [
    `:host{display:flex;flex-direction:column;height:100%}`,
    `mat-form-field.full{width:100%;display:block}`,
    `.content{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}`,
    `.content.mat-dialog-content{max-height:none;margin:0}`,
    `.editor-wrap{flex:1 1 auto;min-height:0;display:flex}`,
    `.editor-wrap editor{flex:1 1 auto;display:flex}`,
    `:host ::ng-deep .editor-wrap .tox-tinymce{height:100% !important;flex:1 1 auto}`,
    `:host ::ng-deep h2[mat-dialog-title]{margin:0 0 8px}`,
    `:host ::ng-deep .mat-dialog-actions{margin:8px 0 0}`,
    `.meta{margin-top:8px;color:#666}`
  ]
})
export class CampaignRecipientPreviewDialog {
  subject: string | null = null;
  body: string | null = null;
  isReadOnly = false;
  saving = false;
  constructor(@NgInject(MAT_DIALOG_DATA) public data: { recipient: CampaignRecipient, campaign: LumaCampaign }, private dialogRef: MatDialogRef<CampaignRecipientPreviewDialog>, @NgInject(LumaService) private luma: LumaService) {
    this.subject = data.recipient.generated_subject;
    this.body = data.recipient.generated_body;
    this.isReadOnly = data.campaign.status === 'pending_send' || data.campaign.status === 'send_complete';
  }
  update() {
    if (this.isReadOnly) { return; }
    this.saving = true;
    this.luma.updateRecipient(this.data.campaign.id, this.data.recipient.id, { generated_subject: this.subject, generated_body: this.body })
      .subscribe({ next: () => { this.saving = false; this.dialogRef.close(true); }, error: () => { this.saving = false; } });
  }
  close() { this.dialogRef.close(); }
}

@NgComponent({
  selector: 'send-campaign-dialog',
  template: `
  <h2 mat-dialog-title>Send Campaign</h2>
  <div mat-dialog-content>
    <mat-form-field appearance="fill" class="full">
      <mat-label>Sender Name</mat-label>
      <input matInput [(ngModel)]="senderName" />
    </mat-form-field>
    <mat-form-field appearance="fill" class="full">
      <mat-label>Sender Email</mat-label>
      <input matInput [(ngModel)]="senderEmail" />
    </mat-form-field>
  </div>
  <div mat-dialog-actions align="end">
    <button mat-button (click)="dialogRef.close()">Cancel</button>
    <button mat-raised-button color="lightGreen" (click)="continue()" [disabled]="!senderName || !senderEmail">Continue</button>
  </div>
  `,
  styles: [
    `mat-form-field.full{width:100%;display:block}`,
  ]
})
export class SendCampaignDialog {
  senderName = '';
  senderEmail = '';
  constructor(@NgInject(MAT_DIALOG_DATA) public data: any, public dialogRef: MatDialogRef<SendCampaignDialog>, @NgInject(LumaService) private luma: LumaService) {}
  continue() {
    this.luma.setSender(this.data.campaignId, { sender_name: this.senderName, sender_email: this.senderEmail }).subscribe(() => this.dialogRef.close(true));
  }
}


